import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, FileText, AlertCircle, ChevronLeft, ChevronRight } from "lucide-react";
import axios from "axios";
import { API } from "@/config";

export function detectKind(filename = "") {
  const ext = (filename.split(".").pop() || "").toLowerCase();
  if (["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(ext)) return { kind: "image", mime: `image/${ext === "jpg" ? "jpeg" : ext}` };
  if (ext === "pdf") return { kind: "pdf", mime: "application/pdf" };
  if (["doc", "docx", "odt"].includes(ext)) return { kind: "office", mime: "" };
  return { kind: "other", mime: "" };
}

const DOC_TYPE_LABELS = {
  cv: "CV", id_card: "Pièce d'identité", carte_sejour: "Carte de séjour",
  attestation_secu: "Attestation Sécu", rib: "RIB",
  justificatif_domicile: "Justificatif domicile",
  certificat_travail: "Certificat travail / bulletins",
  visite_medicale: "Visite médicale", rqth: "RQTH",
  caces_habilitations: "CACES / habilitations",
};

export default function DocumentPreviewDialog({ doc, docs, open, onOpenChange }) {
  // Mode 1: single doc (legacy)  - Mode 2: carousel with docs array
  const list = Array.isArray(docs) && docs.length > 0 ? docs : (doc ? [doc] : []);
  const initialIndex = doc ? Math.max(0, list.findIndex((d) => d.doc_id === doc.doc_id)) : 0;
  const [index, setIndex] = useState(initialIndex);
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (open) setIndex(initialIndex); }, [open, initialIndex]);

  const current = list[index];
  const { kind, mime } = detectKind(current?.original_filename || "");

  useEffect(() => {
    if (!open || !current) return;
    if (kind !== "image" && kind !== "pdf") { setBlobUrl(null); return; }
    let url = null;
    let cancelled = false;
    setLoading(true);
    setError("");
    setBlobUrl(null);
    axios.get(`${API}/documents/${current.doc_id}/download`, { withCredentials: true, responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        const blob = new Blob([r.data], { type: mime || r.headers["content-type"] || "application/octet-stream" });
        url = URL.createObjectURL(blob);
        setBlobUrl(url);
      })
      .catch(() => { if (!cancelled) setError("Impossible de charger l'aperçu"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [open, current, kind, mime]);

  const prev = useCallback(() => { if (list.length > 1) setIndex((i) => (i - 1 + list.length) % list.length); }, [list.length]);
  const next = useCallback(() => { if (list.length > 1) setIndex((i) => (i + 1) % list.length); }, [list.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, prev, next]);

  const handleDownload = () => {
    if (!current) return;
    const a = document.createElement("a");
    a.href = `${API}/documents/${current.doc_id}/download`;
    a.click();
  };

  if (!current) return null;

  const hasNav = list.length > 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl rounded-none max-h-[95vh] overflow-hidden flex flex-col" data-testid="document-preview-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#3F3F3F] flex items-center gap-3 pr-8 flex-wrap">
            <FileText className="w-4 h-4 text-gray-400 shrink-0" />
            <span className="text-sm truncate min-w-0 flex-1">{current.original_filename}</span>
            <span className="text-xs font-normal text-[#662483] bg-[#F0E9F3] px-2 py-1 shrink-0">{DOC_TYPE_LABELS[current.doc_type] || current.doc_type}</span>
            {hasNav && <span className="text-xs font-normal text-gray-500 shrink-0" data-testid="preview-counter">{index + 1} / {list.length}</span>}
            <Button onClick={handleDownload} variant="outline" className="rounded-none text-xs shrink-0" data-testid="preview-download-btn">
              <Download className="w-3.5 h-3.5 mr-1" /> Télécharger
            </Button>
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-auto bg-[#FAFAFA] min-h-[60vh] flex items-center justify-center relative">
          {hasNav && (
            <>
              <button onClick={prev} aria-label="Document précédent" data-testid="preview-prev"
                className="absolute left-2 top-1/2 -translate-y-1/2 z-10 p-2 bg-white/90 hover:bg-white border border-gray-200 shadow-sm hover:shadow-md transition-all">
                <ChevronLeft className="w-5 h-5 text-[#3F3F3F]" />
              </button>
              <button onClick={next} aria-label="Document suivant" data-testid="preview-next"
                className="absolute right-2 top-1/2 -translate-y-1/2 z-10 p-2 bg-white/90 hover:bg-white border border-gray-200 shadow-sm hover:shadow-md transition-all">
                <ChevronRight className="w-5 h-5 text-[#3F3F3F]" />
              </button>
            </>
          )}
          {loading && <p className="text-sm text-gray-500">Chargement…</p>}
          {error && (
            <div className="text-center p-6">
              <AlertCircle className="w-8 h-8 mx-auto mb-2 text-red-400" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
          {!loading && !error && kind === "image" && blobUrl && (
            <img src={blobUrl} alt={current.original_filename} className="max-w-full max-h-[80vh] object-contain" data-testid="preview-image" />
          )}
          {!loading && !error && kind === "pdf" && blobUrl && (
            <iframe src={`${blobUrl}#toolbar=1&navpanes=0`} title={current.original_filename} className="w-full h-[80vh] border-0" data-testid="preview-iframe" />
          )}
          {kind === "office" && (
            <div className="text-center p-8 max-w-md">
              <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm font-semibold text-[#3F3F3F] mb-1">Aperçu Word non disponible</p>
              <p className="text-xs text-gray-500 mb-4">L'aperçu inline des documents Word n'est pas pris en charge par les navigateurs.</p>
              <Button onClick={handleDownload} className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none">
                <Download className="w-4 h-4 mr-2" /> Télécharger le fichier
              </Button>
            </div>
          )}
          {kind === "other" && !loading && (
            <div className="text-center p-8">
              <p className="text-sm text-gray-500 mb-4">Aperçu non disponible pour ce type de fichier.</p>
              <Button onClick={handleDownload} className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none">
                <Download className="w-4 h-4 mr-2" /> Télécharger
              </Button>
            </div>
          )}
        </div>
        {hasNav && (
          <div className="flex items-center justify-center gap-1 p-2 border-t border-gray-100 bg-white overflow-x-auto">
            {list.map((d, i) => (
              <button
                key={d.doc_id}
                onClick={() => setIndex(i)}
                className={`text-[10px] px-2 py-1 border whitespace-nowrap transition-colors ${i === index ? "bg-[#662483] text-white border-[#662483]" : "bg-white text-gray-600 border-gray-200 hover:border-[#662483]"}`}
                data-testid={`preview-thumb-${i}`}
                title={d.original_filename}
              >
                {DOC_TYPE_LABELS[d.doc_type] || d.doc_type}
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
